import { isLoopbackHost } from './loopbackHost';

const normalizeUrl = (value, fallback) => {
  const raw = String(value || fallback || '').trim();
  if (!raw) return '';
  return raw.replace(/\/+$/, '');
};

/** Публичный лендинг / юридические страницы / in-repo HelpPage (`/docs`). Не путать с app.* */
export const PUBLIC_SITE_URL = normalizeUrl(
  process.env.REACT_APP_PUBLIC_SITE_URL,
  'https://markbase.ru'
);

/** Канонический хост портала документации (HelpPage / aidock) */
export const DOCS_PORTAL_URL = normalizeUrl(
  process.env.REACT_APP_DOCS_PORTAL_URL,
  'https://docs.markbase.ru'
);

/** Подпись в футере / auth («example.com» без www) */
export function getPublicSiteHostname() {
  try {
    return new URL(PUBLIC_SITE_URL).hostname.replace(/^www\./, '');
  } catch {
    return 'markbase.ru';
  }
}

export const UAM_URL = normalizeUrl(process.env.REACT_APP_UAM_URL, 'https://auth.markbase.ru');

/** Портал CORE SEO (dashboard, sitemap URLs) */
export const SEO_PUBLIC_URL = normalizeUrl(
  process.env.REACT_APP_SEO_PUBLIC_URL,
  'https://seo.markbase.ru'
);

/** Публичный Captcha-модуль */
export const CAPTCHA_PUBLIC_URL = normalizeUrl(
  process.env.REACT_APP_CAPTCHA_URL,
  'https://captcha.markbase.ru'
);

/** CORE Security — RPM, IP lists, audit (отдельно от App security_global_settings) */
export const SECURITY_PUBLIC_URL = normalizeUrl(
  process.env.REACT_APP_SECURITY_URL,
  'https://security.markbase.ru'
);

/** CORE Registry — discovery, connections, domain_map, runtime-config */
export const REGISTRY_PUBLIC_URL = normalizeUrl(
  process.env.REACT_APP_REGISTRY_URL,
  'https://registry.markbase.ru'
);
export const BILLING_URL = normalizeUrl(process.env.REACT_APP_BILLING_URL, 'https://billing.markbase.ru');
export const WALLET_URL = normalizeUrl(process.env.REACT_APP_WALLET_URL, 'https://wallet.markbase.ru');

/** Разделы Wallet / Billing в браузере (App `RedirectToUrl`, выпадающее меню профиля) */
export const WALLET_PAGE_URL = String(
  process.env.REACT_APP_WALLET_PAGE_URL || `${WALLET_URL}/account/balance`
).replace(/\/+$/, '');

/** Канон UI тарифов — UAM SPA (auth), не billing API host */
export const BILLING_PAGE_URL = String(
  process.env.REACT_APP_BILLING_PAGE_URL || `${UAM_URL}/account/tariffs`
).replace(/\/+$/, '');

export const BILLING_ADMIN_PAGE_URL = String(
  process.env.REACT_APP_BILLING_ADMIN_PAGE_URL || `${UAM_URL}/account/admin/tariffs`
).replace(/\/+$/, '');

/** S2S / health API vhost — не inbox UI. Inbox: `constants/notificationsModuleUrls` → NOTIFICATIONS_APP_INBOX */
export const NOTIFICATIONS_URL = normalizeUrl(process.env.REACT_APP_NOTIFICATIONS_URL, 'https://notifications.markbase.ru');
export const HELP_URL = normalizeUrl(process.env.REACT_APP_HELP_URL, 'https://help.markbase.ru');
export const WAYGPT_URL = normalizeUrl(process.env.REACT_APP_WAYGPT_URL, 'https://app.waygpt.ru');

/** Ссылка «Разработка» в футере маркетинговых страниц модулей */
export const STUDIO_CREDITS_URL = normalizeUrl(
  process.env.REACT_APP_STUDIO_CREDITS_URL,
  'https://w7c.ru'
);

/**
 * Каталог презентаций продуктов экосистемы (Next-сайт студии).
 * На markbase.ru не дублируется — см. `/prezentacii` на этом origin.
 */
export const W7C_ECOSYSTEM_PRESENTATIONS_URL = normalizeUrl(
  process.env.REACT_APP_W7C_ECOSYSTEM_PRESENTATIONS_URL,
  `${STUDIO_CREDITS_URL}/prezentacii`
);

/** Почта поддержки в UI (ошибки, контакты, подсказки модулей) */
export const SUPPORT_EMAIL = String(process.env.REACT_APP_SUPPORT_EMAIL || 'support@markbase.ru').trim();

/** Общие и sales-запросы на лендинге / публичных страницах */
export const PUBLIC_CONTACT_EMAIL = String(
  process.env.REACT_APP_PUBLIC_CONTACT_EMAIL || 'support@markbase.ru'
).trim();

export const SALES_EMAIL = String(process.env.REACT_APP_SALES_EMAIL || 'support@markbase.ru').trim();

/** Портал пользователей / компаний (ядро UAM-контур в проде) */
export const USERS_PORTAL_URL = normalizeUrl(
  process.env.REACT_APP_USERS_PORTAL_URL,
  'https://users.markbase.ru'
);

/** Embedded FEED-модуль — Молвинка (molvinka.ru) */
export const FEED_MODULE_URL = normalizeUrl(
  process.env.REACT_APP_FEED_MODULE_URL,
  'https://molvinka.ru'
);

/**
 * Делграм — публичная витрина маркетплейса MPLAZA (delgram.ru).
 * Канон совпадает с `MPLAZA_PUBLIC_URL` / `MPLAZA_FRONTEND_URL` в backend — не поддомен `*.markbase.ru`.
 */
export const DELGRAM_URL = normalizeUrl(
  process.env.REACT_APP_DELGRAM_URL,
  'https://delgram.ru'
);

/** Явный alias для кода и поиска: «сайт MPLAZA» = Делграм (тот же origin). */
export const MPLAZA_PUBLIC_SITE_URL = DELGRAM_URL;

/** Embedded CRM-модуль (standalone Next.js, crm.markbase.ru) */
export const CRM_MODULE_URL = normalizeUrl(
  process.env.REACT_APP_CRM_MODULE_URL,
  'https://crm.markbase.ru/admin/dashboard'
);

/**
 * Embedded HRM-модуль (standalone Next.js, hrm.markbase.ru).
 * Управление персоналом: оргструктура, сотрудники, должности, KPI, зарплата,
 * договоры, отпуска, телефония, AI-ассистенты HR. Бэкенд `modouls/HRM/backend`
 * (порт `8068`, БД `hrm_db`), vhost `nginx/conf.d/hrm.markbase.conf`.
 * Master-employee truth — только в HRM (`hrm.employees`); смежные модули
 * (Auth, Tasks, Phone, Chats, KPI) ссылаются по `employee_id`.
 */
export const HRM_MODULE_URL = normalizeUrl(
  process.env.REACT_APP_HRM_MODULE_URL,
  'https://hrm.markbase.ru/admin/dashboard'
);

/**
 * Embedded LEGAL-модуль (standalone Next.js, legal.markbase.ru).
 * Юридический контур компании: договоры (master legal contracts), претензии
 * (claims), судебная работа (court), AI-ассистент юриста, история сессий,
 * загрузка документов, анализ. Бэкенд `modouls/LEGAL/backend` (порт `8091`,
 * БД `legal_db`), vhost `nginx/conf.d/legal.markbase.conf` + собственный
 * Docker nginx (`modouls/LEGAL/nginx`).
 *
 * Не путать со статикой `/privacy`, `/terms`, `/cookies` лендинга и
 * `components/Legal/CookieBanner` — это другой контур (публичные юр-документы
 * платформы).
 */
export const LEGAL_MODULE_URL = normalizeUrl(
  process.env.REACT_APP_LEGAL_MODULE_URL,
  'https://legal.markbase.ru/dashboard'
);

/** Embedded DELIVERY-модуль (standalone Next.js, delivery.markbase.ru) */
export const DELIVERY_MODULE_URL = normalizeUrl(
  process.env.REACT_APP_DELIVERY_MODULE_URL,
  'https://delivery.markbase.ru/admin/dashboard'
);

/**
 * Embedded LOGISTICS-модуль (Next.js админка, `logistics.markbase.ru`).
 * Управление маршрутами, водителями, диспетчеризация, грузоперевозки.
 * Бэкенд — `modouls/LOGISTICS/backend` (nginx host `127.0.0.1:8090`, БД `logistics_db`).
 */
export const LOGISTICS_MODULE_URL = normalizeUrl(
  process.env.REACT_APP_LOGISTICS_MODULE_URL,
  'https://logistics.markbase.ru/admin/dashboard'
);

/** Витрина Shop (внешний модуль) */
export const SHOP_SITE_URL = normalizeUrl(process.env.REACT_APP_SHOP_SITE_URL, 'https://shop.markbase.ru');

export const SHOP_ADMIN_DASHBOARD_URL = normalizeUrl(
  process.env.REACT_APP_SHOP_ADMIN_URL,
  `${SHOP_SITE_URL}/admin/dashboard`
);

/** Embedded-точка входа Shop внутри App */
export const SHOP_MODULE_ROOT_URL = normalizeUrl(
  process.env.REACT_APP_SHOP_MODULE_URL,
  SHOP_ADMIN_DASHBOARD_URL
);

/** Публичный корень модуля SERVICES (админка расписаний / маркетплейс услуг) */
export const SERVICES_MODULE_ROOT_URL = normalizeUrl(
  process.env.REACT_APP_SERVICES_MODULE_URL,
  'https://services.markbase.ru'
);

/** Embedded Bonus-модуль внутри App */
export const BONUS_MODULE_ROOT_URL = normalizeUrl(
  process.env.REACT_APP_BONUS_MODULE_URL,
  'https://bonus.markbase.ru'
);

/** Embedded Search-модуль внутри App */
export const SEARCH_MODULE_ROOT_URL = normalizeUrl(
  process.env.REACT_APP_SEARCH_MODULE_URL,
  'https://search.markbase.ru'
);

/**
 * Публичный API-контур INTEGRATIONS (integration.markbase.ru).
 * Legacy 1С/СБИС — нативно в App `/integrations`; операторский контур — embedded SPA ниже.
 */
export const INTEGRATIONS_PUBLIC_URL = normalizeUrl(
  process.env.REACT_APP_INTEGRATIONS_PUBLIC_URL,
  'https://integration.markbase.ru'
);

/** Embedded INTEGRATIONS admin (Next.js, integration.markbase.ru/admin/*) */
export const INTEGRATIONS_ADMIN_MODULE_URL = normalizeUrl(
  process.env.REACT_APP_INTEGRATIONS_MODULE_URL,
  `${INTEGRATIONS_PUBLIC_URL}/admin`
);

/** Embedded TORG-модуль (тендеры, площадки, мои предложения) */
export const TORG_MODULE_ROOT_URL = normalizeUrl(
  process.env.REACT_APP_TORG_MODULE_URL,
  'https://torg.markbase.ru'
);

/**
 * B2B MARKET — закупки, поставщики, RFQ, сделки (standalone SPA, market.markbase.ru).
 * Не путать с App `/marketplaces` (INTEGRATIONS: Ozon/WB/Яндекс).
 */
export const MARKET_MODULE_URL = normalizeUrl(
  process.env.REACT_APP_MARKET_MODULE_URL,
  'https://market.markbase.ru/suppliers'
);

export const MARKET_CONNECTION_URL = `${normalizeUrl(
  process.env.REACT_APP_MARKET_PUBLIC_URL,
  'https://market.markbase.ru'
)}/settings/connection`;

/**
 * MPLAZA (Delgram) — инструкция подключения в App shell.
 * Публичная витрина — DELGRAM_URL / REACT_APP_DELGRAM_URL.
 */
export const MPLAZA_APP_CONNECTION_URL = `${normalizeUrl(
  process.env.REACT_APP_API_URL,
  'https://app.markbase.ru'
).replace(/\/api\/?$/, '')}/product/mplaza/connection`;

export const MPLAZA_SELLER_CONNECTION_URL = `${normalizeUrl(
  process.env.REACT_APP_MPLAZA_PUBLIC_URL || process.env.REACT_APP_DELGRAM_URL,
  DELGRAM_URL
)}/seller/connection`;

/**
 * Embedded ORDERS admin (Next.js фронт `modouls/ORDERS/frontend` →
 * `orders-frontend:80`, домен `https://orders.markbase.ru/admin/dashboard`).
 *
 * Это операционная админка модульного backend `modouls/ORDERS` (управление
 * заказами поставщикам, ETM/RS24, supplier-orders). App `/orders/*` редиректит
 * в `/modules/orders` (модуль ORDERS — SSOT операционной админки).
 */
export const ORDERS_ADMIN_MODULE_URL = normalizeUrl(
  process.env.REACT_APP_ORDERS_ADMIN_MODULE_URL,
  'https://orders.markbase.ru/admin/dashboard'
);

/**
 * Embedded SKLAD WMS-модуль (планы, задачи, ячейки, инвентаризация, списания,
 * визуальный планировщик). Standalone Vite-фронт `modouls/SKLAD/frontend` +
 * backend `sklad-backend:8096`. Прокси/корневой location — в
 * `markbase.ru/nginx/conf.d/sklad.markbase.conf`.
 */
export const SKLAD_MODULE_ROOT_URL = normalizeUrl(
  process.env.REACT_APP_SKLAD_MODULE_URL,
  'https://sklad.markbase.ru'
);

/** Дашборды markbaseCORE — те же env, что в App.js / compose */
export const SCHEDULER_DASHBOARD_URL = normalizeUrl(
  process.env.REACT_APP_CORE_SCHEDULER_URL,
  'https://scheduler.markbase.ru/dashboard'
);

export const BACKUP_DASHBOARD_URL = normalizeUrl(
  process.env.REACT_APP_CORE_BACKUP_URL,
  'https://backup.markbase.ru/dashboard'
);

/**
 * CORE_monitoring (№48) — FastAPI hot telemetry на monitoring.markbase.ru (:8063).
 * HTML dashboard `/dashboard` — только platform_admin (UAM).
 * Не путать с PROCESS_CENTER embed (`/admin/process-center`, Node :8103).
 */
export const CORE_MONITORING_PUBLIC_URL = normalizeUrl(
  process.env.REACT_APP_CORE_MONITORING_PUBLIC_URL,
  'https://monitoring.markbase.ru'
);

export const CORE_MONITORING_DASHBOARD_URL = `${CORE_MONITORING_PUBLIC_URL.replace(/\/+$/, '')}/dashboard`;

/** PROCESS_CENTER embed (Node :8103) — SSOT в constants/monitoringUrls.js */
export { MONITORING_MODULE_ROOT_URL } from '../constants/monitoringUrls';

/** CORE Plugin Hub — каталог публичных плагинов (SSR на plugins.markbase.ru) */
export const PLUGIN_HUB_URL = normalizeUrl(
  process.env.REACT_APP_PLUGIN_HUB_URL,
  'https://plugins.markbase.ru'
);

/**
 * Абсолютный URL на основном (не app) домене: `/docs`, `/privacy`, главная и т.д.
 * На `app.*` убирается префикс `app.`; на loopback — база из `REACT_APP_PUBLIC_SITE_URL`.
 */
export function resolvePublicSiteUrl(path = '/') {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (typeof window === 'undefined') {
    return `${PUBLIC_SITE_URL}${normalizedPath}`;
  }
  const { protocol, hostname } = window.location;
  if (isLoopbackHost(hostname)) {
    return `${PUBLIC_SITE_URL}${normalizedPath}`;
  }
  const publicHost = hostname.replace(/^app\./, '').replace(/^www\./, '');
  return `${protocol}//${publicHost}${normalizedPath}`;
}

/**
 * Канон URL документации: docs.markbase.ru (не markbase.ru/docs на проде).
 */
export function resolveDocsPortalUrl(path = '/docs') {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (typeof window !== 'undefined') {
    const host = String(window.location.hostname || '').toLowerCase();
    if (host === 'docs.markbase.ru' || isLoopbackHost(host)) {
      return `${window.location.protocol}//${host}${normalizedPath}`;
    }
  }
  return `${DOCS_PORTAL_URL}${normalizedPath}`;
}

/**
 * true на markbase.ru / www — редирект /docs → docs.markbase.ru.
 * По умолчанию false (HelpPage на markbase.ru/docs), пока docs SPA не на проде.
 * Включить после деплоя docs.markbase.ru: REACT_APP_DOCS_PORTAL_REDIRECT=1
 */
export function shouldRedirectMarketingDocsToPortal() {
  if (process.env.REACT_APP_DOCS_PORTAL_REDIRECT !== '1') {
    return false;
  }
  if (typeof window === 'undefined') return false;
  const host = String(window.location.hostname || '').toLowerCase();
  return host === 'markbase.ru' || host === 'www.markbase.ru';
}

